// fingerprintManager.js (ESM, plain JS)
// Drop-in manager replacing clientjs with FingerprintJS (OSS).
// Exposes: preload(), get(), peek()

import FingerprintJS from '@fingerprintjs/fingerprintjs';

const KEY = 'gist:device_id:v1';

let _id = null;
let _promise = null;

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

const safeGet = (k) => {
  try { return isBrowser ? window.localStorage.getItem(k) : null; } catch { return null; }
};

const safeSet = (k, v) => {
  try { if (isBrowser) window.localStorage.setItem(k, v); } catch {}
};

const randHex = (bytes = 16) => {
  const a = new Uint8Array(bytes);
  (isBrowser && window.crypto?.getRandomValues) ? crypto.getRandomValues(a) : a.fill(0);
  return Array.from(a, b => b.toString(16).padStart(2, '0')).join('');
};

const compute = async () => {
  if (!isBrowser) {
    // Non-browser environment: generate a fallback ID
    // This ensures consistent userId_fingerprint format even in SSR/testing
    const fallbackId = randHex(16);
    _id = fallbackId;
    return _id;
  }

  // 1) read any cached ID, but don't commit to _id yet so we can prefer FPJS
  const cached = safeGet(KEY);

  // 2) try FPJS
  let resolvedId = null;
  try {
    const agent = await FingerprintJS.load();
    const { visitorId } = await agent.get();
    if (visitorId) {
      resolvedId = visitorId;
    }
  } catch (err) {
    // ignore and fall back
  }

  // 3) Always ensure we have a fingerprint - never return null
  // This maintains consistent orgId_userId_fingerprint format on backend
  if (!resolvedId) {
    resolvedId = cached || randHex(16);
  }

  _id = resolvedId;
  if (_id) safeSet(KEY, _id);
  return _id;
};

export const preload = () => {
  if (!isBrowser) return;
  _promise ??= compute();
};

export const get = () => {
  // If a computation is in-flight, wait for it (ensures we prefer visitorId over cached fallback)
  if (_promise) return _promise;
  if (_id) return Promise.resolve(_id);
  _promise = compute();
  return _promise;
};

export const peek = () => _id || safeGet(KEY) || null;

export default { preload, get, peek };
